import { Action, Thunk, action, thunk } from "easy-peasy";

import {
  addUnsub,
  multiSubscribe,
  unsubAll,
  unsubOne,
} from "./subscriptionManager";
import { type DataSnapshot, type Unsubscribe } from "../dependencies/firebase";
import { Logger } from "../dependencies/logger";
import { type ByRestaurantId } from "../model/types";
import type { UnsubsByPath } from "./subscriptionManager/types";
import { type AvailabilitySearchType } from "../gql/getters/allAvailabilitySearch";
import type { ArrayElement, NonEmptyArray } from "../types";

const logger = new Logger(
  "RESTAURANT_STORE - FIREBASE - REGULAR_TABLE_SYNC",
  "LOG",
);
// logger.enable();

type AvailabilitySearchSlot = NonNullable<
  ArrayElement<NonNullable<AvailabilitySearchType["availableTimes"]>>
>;

export type FirebaseRegularTableSyncSlot = {
  available: boolean;
  availableTimes?: AvailabilitySearchSlot[];
  dataStatus: AvailabilitySearchType["dataStatus"];
  syncExpires?: string;
  date: string;
  id: string;
  people: string;
  restaurantId: string;
};

type PathDefinition = {
  restaurantId: number;
  date: string;
  people: number;
  onlyOnce: boolean;
};

function definitionToPath({
  restaurantId,
  date,
  people,
}: Omit<PathDefinition, "onlyOnce">) {
  const path = `regularTableSync/${restaurantId}/${date}/${people}/`;

  return path;
}

function getDefinitions(
  restaurantIds: number[],
  dates: NonEmptyArray<string>,
  people: number,
  onlyOnceById: ByRestaurantId<boolean>,
): PathDefinition[] {
  const definitions: PathDefinition[] = [];

  for (const restaurantId of restaurantIds) {
    const onlyOnce = onlyOnceById[restaurantId] || false;

    for (const date of dates) {
      definitions.push({
        restaurantId,
        date,
        people,
        onlyOnce,
      });
    }
  }

  return definitions;
}

function getAvailabilityFromSnapshot(
  snapShot: DataSnapshot | undefined,
  path: string,
): FirebaseRegularTableSyncSlot | null {
  void path;
  const availability = snapShot?.val();

  if (!availability) {
    return null;
  }

  // change the data format to better match the availability search API
  const formattedSlot = {
    available: !!availability.Available,
    availableTimes: availability.AvailableTimes
      ? (JSON.parse(availability.AvailableTimes) as AvailabilitySearchSlot[]) // TODO: should maybe use Zod instead of "as"
      : undefined,
    dataStatus:
      availability.DataStatus?.toUpperCase() as AvailabilitySearchType["dataStatus"],
    syncExpires: availability.SyncExpires,
    date: availability.Date,
    id: availability.ID,
    people: availability.People,
    restaurantId: availability.RestaurantID,
  };

  return formattedSlot;
}

export type FirebaseModel = {
  unsubsByPath: UnsubsByPath;
  addUnsub: Action<FirebaseModel, { path: string; unsub: Unsubscribe }>;
  unsubOne: Action<FirebaseModel, string>;
  unsubAll: Action<FirebaseModel>;

  subscribe: Thunk<
    FirebaseModel,
    {
      restaurantIds: number[];
      dates: NonEmptyArray<string>;
      people: number;
      onlyOnceById: ByRestaurantId<boolean>;
      onValue: ({
        restaurantId,
        date,
        people,
        availability,
      }: {
        restaurantId: number;
        date: string;
        people: number;
        availability: FirebaseRegularTableSyncSlot | null;
      }) => Promise<void>;
      abortSignal: AbortSignal;
    }
  >;
};

export const firebaseModel: FirebaseModel = {
  unsubsByPath: {},
  addUnsub: action((state, { path, unsub }) => {
    addUnsub(state.unsubsByPath, path, unsub);
  }),
  unsubOne: action((state, path) => {
    unsubOne(state.unsubsByPath, path);
  }),
  unsubAll: action((state) => {
    unsubAll(state.unsubsByPath);
  }),

  subscribe: thunk(
    async (
      actions,
      { restaurantIds, dates, people, onlyOnceById, onValue, abortSignal },
      { getState },
    ) => {
      if (abortSignal.aborted) {
        return;
      }

      logger.log("Subscribing...", {
        restaurantIds,
        dates,
        people,
        onlyOnceById,
      });

      const newDefinitions = getDefinitions(
        restaurantIds,
        dates,
        people,
        onlyOnceById,
      );

      await multiSubscribe(
        newDefinitions,
        getState().unsubsByPath,
        actions.unsubOne,
        (path, unsub) => actions.addUnsub({ path, unsub }),
        definitionToPath,
        getAvailabilityFromSnapshot,
        onValue,
        abortSignal,
      );
    },
  ),
};
